import { g } from '@hcie/core';
import type { ToolPropConfig } from '@hcie/core';
import { saveToolSettings, loadToolSettings, syncStateToSettings } from '@hcie/shared';


// Removed DOMContentLoaded listener to avoid race conditions.
// initializePropertiesPanel is now called explicitly from main.ts.

export function initializePropertiesPanel() {
    console.log('Properties panel initializing...');
    loadToolSettings();

    const container = document.getElementById('dynamic-properties');
    if (!container) return;

    // Helper to setup a dynamic slider
    function createSlider(config: any) {
        const group = document.createElement('div');
        group.className = 'property-group';

        const label = document.createElement('label');
        label.className = 'property-label';
        label.textContent = config.label;

        const control = document.createElement('div');
        control.className = 'property-control';

        const slider = document.createElement('input');
        slider.type = 'range';
        slider.className = 'property-slider';
        slider.min = config.min.toString();
        slider.max = config.max.toString();
        slider.id = `dynamic-${config.prop}`;

        // Get initial value
        let currentVal = (g as any)[config.prop];
        if (config.mapping === 'percent') {
            slider.value = Math.round(currentVal * 100).toString();
        } else {
            slider.value = currentVal.toString();
        }

        const valueInput = document.createElement('input');
        valueInput.type = 'number';
        valueInput.className = 'property-value-input';
        valueInput.style.cssText = `
            width: 45px;
            font-size: 11px;
            background: var(--bg-input);
            color: var(--text-primary);
            border: 1px solid var(--border-color);
            border-radius: 2px;
            text-align: right;
            margin-left: 8px;
        `;
        valueInput.value = slider.value;

        const syncValue = (val: string, source = 'properties') => {
            slider.value = val;
            valueInput.value = val;

            if (config.mapping === 'percent') {
                (g as any)[config.prop] = parseFloat(val) / 100;
            } else {
                (g as any)[config.prop] = parseInt(val);
            }

            // Dispatch specific sync events
            if (config.prop === 'brush_hardness') {
                window.dispatchEvent(new CustomEvent('hardnessChanged', { detail: { value: (g as any)[config.prop] } }));
            }
            if (config.prop === 'pen_width') {
                window.dispatchEvent(new CustomEvent('penWidthChanged', { detail: { value: (g as any)[config.prop], source } }));
            }
            if (config.prop === 'round_rect_corner_radius') {
                window.dispatchEvent(new CustomEvent('cornerRadiusChanged', { detail: { value: (g as any)[config.prop] } }));
            }
            if (config.prop === 'pen_opacity') {
                window.dispatchEvent(new CustomEvent('syncOpacity', { detail: { value: val, source } }));
            }
            if (config.prop === 'text_size') {
                window.dispatchEvent(new CustomEvent('textPropertyChanged', { detail: { prop: 'size', value: parseInt(val) } }));
            }
            // Save settings after change
            const settings = loadToolSettings();
            syncStateToSettings(g, settings);
            saveToolSettings(settings);
        };

        slider.addEventListener('input', () => syncValue(slider.value));
        
        // Listen for changes from top options bar
        const onExternalChange = (e: any) => {
            if (e.detail.source === 'options') {
                let newVal = e.detail.value;
                if (config.prop === 'pen_opacity') newVal = e.detail.value;
                syncValue(newVal.toString(), 'options');
            }
        };
        if (config.prop === 'pen_width') window.addEventListener('penWidthChanged', onExternalChange);
        if (config.prop === 'pen_opacity') window.addEventListener('syncOpacity', onExternalChange);
        valueInput.addEventListener('change', () => {
            let val = parseInt(valueInput.value);
            if (isNaN(val)) val = config.min;
            val = Math.max(config.min, Math.min(config.max, val));
            syncValue(val.toString());
        });

        control.appendChild(slider);
        control.appendChild(valueInput);
        group.appendChild(label);
        group.appendChild(control);
        return group;
    }

    // Helper to setup a dynamic select
    function createSelect(config: any) {
        const group = document.createElement('div');
        group.className = 'property-group';

        const label = document.createElement('label');
        label.className = 'property-label';
        label.textContent = config.label;

        const control = document.createElement('div');
        control.className = 'property-control';

        const select = document.createElement('select');
        select.style.width = '100%';
        select.style.fontSize = '11px';

        config.items.forEach((item: string) => {
            const option = document.createElement('option');
            option.value = item;
            option.textContent = item;
            if ((g as any)[config.prop] === item) option.selected = true;
            select.appendChild(option);
        });

        select.addEventListener('change', (e) => {
            const target = e.target as HTMLSelectElement;
            (g as any)[config.prop] = target.value;
            if (config.prop === 'text_font') {
                window.dispatchEvent(new CustomEvent('textPropertyChanged', { detail: { prop: 'font', value: target.value } }));
            }
            // Save settings after change
            const settings = loadToolSettings();
            syncStateToSettings(g, settings);
            saveToolSettings(settings);
        });

        control.appendChild(select);
        group.appendChild(label);
        group.appendChild(control);
        return group;
    }

    // Helper to setup a dynamic checkbox
    function createCheckbox(config: any) {
        const group = document.createElement('div');
        group.className = 'property-group';
        group.style.flexDirection = 'row';
        group.style.alignItems = 'center';
        group.style.justifyContent = 'space-between';
        group.style.padding = '4px 0';

        const label = document.createElement('label');
        label.className = 'property-label';
        label.textContent = config.label;
        label.style.margin = '0';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = (g as any)[config.prop];

        const controlWrapper = document.createElement('div');
        controlWrapper.style.display = 'flex';
        controlWrapper.style.alignItems = 'center';
        controlWrapper.style.gap = '8px';

        if (config.prop === 'cyclic_color') {
            const rainbowIcon = document.createElement('div');
            rainbowIcon.style.width = '16px';
            rainbowIcon.style.height = '16px';
            rainbowIcon.style.borderRadius = '2px';
            rainbowIcon.style.background = 'linear-gradient(to right, red, orange, yellow, green, blue, indigo, violet)';
            rainbowIcon.style.marginRight = '4px';
            rainbowIcon.title = 'Cyclic Rainbow Mode';
            controlWrapper.appendChild(rainbowIcon);
        }

        checkbox.addEventListener('change', (e) => {
            const target = e.target as HTMLInputElement;
            (g as any)[config.prop] = target.checked;
            if (config.prop === 'shape_fill') {
                window.dispatchEvent(new CustomEvent('fillChanged', { detail: { value: (g as any)[config.prop] } }));
                // Persist globally (independent from tool-settings v2) so it survives restart.
                (window as any).settingsManager?.set?.('vector.fillShape', target.checked);
            }
            // Save settings after change
            const settings = loadToolSettings();
            syncStateToSettings(g, settings);
            saveToolSettings(settings);
        });

        controlWrapper.appendChild(checkbox);
        group.appendChild(label);
        group.appendChild(controlWrapper);
        return group;
    }

    // Props controlled by the top options bar (Size, Opacity) — removed filter as requested
    const TOOLBAR_ONLY_PROPS: string[] = [];

    // Vector Select: show only props that apply to the selected shape type (values synced from shape.style)
    const VECTOR_SHAPE_PROPS: any = {
        line: ['pen_width', 'pen_opacity', 'brush_hardness'],
        rect: ['pen_width', 'pen_opacity', 'brush_hardness', 'shape_fill'],
        circle: ['pen_width', 'pen_opacity', 'brush_hardness', 'shape_fill'],
        ellipse: ['pen_width', 'pen_opacity', 'brush_hardness', 'shape_fill'],
        roundrect: ['pen_width', 'pen_opacity', 'brush_hardness', 'round_rect_corner_radius', 'shape_fill']
    };

    function createGeometryGroup(sel: any) {
        const group = document.createElement('div');
        group.className = 'property-section';
        group.innerHTML = `<h4 style="font-size: 11px; color: var(--text-secondary); margin: 12px 0 8px 0; border-bottom: 1px solid var(--border-color); padding-bottom: 4px;">Geometry [#1344]</h4>`;

        const grid = document.createElement('div');
        grid.style.display = 'grid';
        grid.style.gridTemplateColumns = '1fr 1fr';
        grid.style.gap = '8px';

        // Dynamic fields based on shape type
        let fields: Array<{label: string, prop: string}> = [
            { label: 'X1', prop: 'x1' }, { label: 'Y1', prop: 'y1' },
            { label: 'X2', prop: 'x2' }, { label: 'Y2', prop: 'y2' },
        ];
        if (sel.type === 'circle') {
            fields.push({ label: 'Radius', prop: 'radius' });
        } else if (sel.type === 'line') {
            fields.push({ label: 'Angle', prop: 'angle' });
        } else {
            // rect, ellipse, roundrect
            fields.push({ label: 'Angle', prop: 'angle' });
        }

        fields.forEach(f => {
            const item = document.createElement('div');
            item.className = 'property-group';
            item.style.flexDirection = 'column';
            item.style.alignItems = 'flex-start';

            const label = document.createElement('label');
            label.className = 'property-label';
            label.textContent = f.label;
            label.style.fontSize = '10px';

            const input = document.createElement('input');
            input.type = 'number';
            input.id = `geo-${f.prop}`;
            input.className = 'property-value-input';
            input.style.width = '100%';
            input.style.marginLeft = '0';
            input.style.cursor = 'ew-resize';

            // Drag-to-adjust numeric inputs (horizontal scrub).
            let scrubActive = false;
            let scrubStartX = 0;
            let scrubStartVal = 0;
            const scrubSpeed = (prop: string) => {
                if (prop === 'angle') return 0.5;
                if (prop === 'radius') return 0.5;
                return 1;
            };
            input.addEventListener('mousedown', (ev: MouseEvent) => {
                if (ev.button !== 0) return;
                ev.preventDefault();
                scrubActive = true;
                scrubStartX = ev.clientX;
                scrubStartVal = parseFloat(input.value || '0') || 0;
                input.style.cursor = 'ew-resize';
                document.body.style.cursor = 'ew-resize';
                const onMove = (e: MouseEvent) => {
                    if (!scrubActive) return;
                    const dx = e.clientX - scrubStartX;
                    const next = scrubStartVal + dx * scrubSpeed(f.prop);
                    input.value = Math.round(next).toString();
                    input.dispatchEvent(new Event('change'));
                };
                const onUp = () => {
                    scrubActive = false;
                    document.body.style.cursor = '';
                    document.removeEventListener('mousemove', onMove, true);
                    document.removeEventListener('mouseup', onUp, true);
                };
                document.addEventListener('mousemove', onMove, true);
                document.addEventListener('mouseup', onUp, true);
            });
            
            if (f.prop === 'angle') {
                // Line shape: angle comes from geometry, not style.angle
                if (sel.type === 'line') {
                    const a = Math.round(Math.atan2(sel.y2 - sel.y1, sel.x2 - sel.x1) * 180 / Math.PI);
                    input.value = a.toString();
                } else {
                    input.value = (sel.style?.angle || 0).toString();
                }
            } else if (f.prop === 'radius') {
                const r = Math.round(Math.hypot(sel.x2 - sel.x1, sel.y2 - sel.y1));
                input.value = r.toString();
            } else {
                input.value = Math.round((sel as any)[f.prop] || 0).toString();
            }

            input.addEventListener('change', () => {
                const val = parseFloat(input.value);
                const updates: any = {};
                updates[f.prop] = val;
                
                const toolCtx = { g, layers: (window as any).layers, activeLayerIndex: g.activeLayerIndex, zoom: g.zoom, tempCtx: null };
                if ((window as any).vectorToolManager) {
                    (window as any).vectorToolManager.updateShapeGeometry(toolCtx, updates);
                }
            });

            item.appendChild(label);
            item.appendChild(input);
            grid.appendChild(item);
        });

        group.appendChild(grid);
        return group;
    }

    function getPropConfigsForShapeType(shapeType: string) {
        const propNames = VECTOR_SHAPE_PROPS[shapeType];
        if (!propNames || propNames.length === 0) return [];
        const vectorConfig = window.g.toolConfig['btn-vector-select'];
        if (!vectorConfig || !vectorConfig.props) return [];
        return propNames
            .map((name: string) => vectorConfig.props.find((p: any) => p.prop === name))
            .filter(Boolean);
    }

    function updatePanelForVectorSelection() {
        if (!container) return;
        container.innerHTML = '';
        const sel = (window as any).vectorSelection || { hasSelection: false, shapeType: null, style: null };
        if (!sel.hasSelection || !sel.shapeType || !sel.style) {
            container.innerHTML = `<p style="color: #999; font-size: 11px; text-align: center; padding: 10px;">Select a shape to edit</p>`;
            return;
        }

        // Sync shape style to global state so sliders show correct values
        if (sel.style.width !== undefined) (g as any).pen_width = sel.style.width;
        if (sel.style.opacity !== undefined) (g as any).pen_opacity = sel.style.opacity;
        if (sel.style.hardness !== undefined) (g as any).brush_hardness = sel.style.hardness;
        if (sel.style.cornerRadius !== undefined) (g as any).round_rect_corner_radius = sel.style.cornerRadius;
        if (sel.style.fill !== undefined) (g as any).shape_fill = sel.style.fill;
        if (sel.style.color !== undefined) (g as any).pen_color = sel.style.color;
        if (sel.style.fillColor !== undefined) (g as any).pen_secondary_color = sel.style.fillColor;

        // Add Geometry Section
        container.appendChild(createGeometryGroup(sel));

        const propConfigs = getPropConfigsForShapeType(sel.shapeType);
        if (propConfigs.length === 0) {
            // If no style props, don't show empty msg if we already have geometry
            return;
        }
        propConfigs.forEach((propConfig: ToolPropConfig) => {
            if (propConfig.type === 'select') {
                container.appendChild(createSelect(propConfig));
            } else if (propConfig.type === 'checkbox') {
                container.appendChild(createCheckbox(propConfig));
            } else {
                container.appendChild(createSlider(propConfig));
            }
        });
    }

    function updatePanelForTool(toolId: string) {
        if (!container) return;
        container.innerHTML = '';
        if (toolId === 'btn-vector-select') {
            updatePanelForVectorSelection();
            return;
        }
        const config = g.toolConfig[toolId];
        if (!config || !config.props || config.props.length === 0) {
            container.innerHTML = `<p style="color: #999; font-size: 11px; text-align: center; padding: 10px;">No extra options</p>`;
            return;
        }
        const panelProps = config.props.filter((p: ToolPropConfig) => !TOOLBAR_ONLY_PROPS.includes(p.prop));
        if (panelProps.length === 0) {
            container.innerHTML = `<p style="color: #999; font-size: 11px; text-align: center; padding: 10px;">No extra options</p>`;
            return;
        }
        panelProps.forEach((propConfig: ToolPropConfig) => {
            if (propConfig.type === 'select') {
                container.appendChild(createSelect(propConfig));
            } else if (propConfig.type === 'checkbox') {
                container.appendChild(createCheckbox(propConfig));
            } else {
                container.appendChild(createSlider(propConfig));
            }
        });

        // Quick settings: vector ghost mode on raster layers
        const isVectorTool = ['btn-line', 'btn-circle', 'btn-rect', 'btn-rounded-rect', 'btn-ellipse'].includes(toolId);
        if (isVectorTool) {
            const row = document.createElement('div');
            row.className = 'property-group';
            row.style.flexDirection = 'row';
            row.style.alignItems = 'center';
            row.style.justifyContent = 'space-between';
            row.style.padding = '6px 0';

            const label = document.createElement('label');
            label.className = 'property-label';
            label.textContent = 'Vector Ghost Mode (Raster)';
            label.style.margin = '0';

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.style.cursor = 'pointer';
            const sm = (window as any).settingsManager;
            input.checked = !!sm?.get?.('vector.ghostMode');
            input.onchange = (e: any) => {
                sm?.set?.('vector.ghostMode', !!e.target.checked);
            };

            row.appendChild(label);
            row.appendChild(input);
            container.appendChild(row);
        }
    }

    if (container) {
        window.addEventListener('toolChanged', (e: any) => {
            updatePanelForTool(e.detail.tool);
        });
        window.addEventListener('vectorShapeSelectionChanged', () => {
            if (g && g.current_tool && (g.current_tool.id === 'btn-vector-select' || g.current_tool.id.startsWith('btn-'))) {
                // Refresh if a vector tool is active to show geometry
                const isVectorTool = ['btn-line', 'btn-circle', 'btn-rect', 'btn-rounded-rect', 'btn-ellipse'].includes(g.current_tool.id);
                if (isVectorTool || g.current_tool.id === 'btn-vector-select') {
                    updatePanelForVectorSelection();
                }
            }
        });

        window.addEventListener('vectorShapeGeometryChanged', (e: any) => {
            const data = e.detail;
            ['x1', 'y1', 'x2', 'y2'].forEach(prop => {
                const el = document.getElementById(`geo-${prop}`) as HTMLInputElement;
                if (el && document.activeElement !== el) {
                    el.value = Math.round(data[prop]).toString();
                }
            });
            // angle field: always use the pre-calculated value from event (already atan2 for lines)
            const angleEl = document.getElementById('geo-angle') as HTMLInputElement;
            if (angleEl && document.activeElement !== angleEl) {
                angleEl.value = Math.round(data.angle).toString();
            }
            // radius field for circles
            const radiusEl = document.getElementById('geo-radius') as HTMLInputElement;
            if (radiusEl && document.activeElement !== radiusEl && data.type === 'circle') {
                const r = Math.round(Math.hypot(data.x2 - data.x1, data.y2 - data.y1));
                radiusEl.value = r.toString();
            }
        });

        if (g && g.current_tool) {
            updatePanelForTool(g.current_tool.id);
        }

        // Listen for settings being restored from localStorage
        window.addEventListener('settingsRestored', () => {
            if (g && g.current_tool) {
                updatePanelForTool(g.current_tool.id);
            }
        });
    }

    console.log('Properties panel dynamic logic initialized');
}

// Export for use in other scripts
if (typeof window !== 'undefined') {
    
    // Expose for debugging/manual calls if needed
    // We need to extract it from the closure or attach it to window inside the closure
}
